import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, del, get, patch, post, put, upload, useSession, type ApiBody, type ApiResponse } from '@agribot/polygon-checks-ui'
import type { HelpRequest } from '@agribot/sourcing-model'

/**
 * The buyer workspace's calls to eudr-api: the taxonomy, campaigns, parent
 * companies, who reviews each supplier, and supplier help requests. Shapes
 * come from the API's own OpenAPI document (scripts/api-types.sh) where it
 * declares them; all camelCase.
 */

export type StaffRole = 'admin' | 'buyer' | 'regional_lead' | 'rnd' | 'viewer'

export function useRoles(): StaffRole[] {
  const { data } = useSession()
  return data?.roles ?? []
}

/** Outside codes by scheme: eu_feed_catalogue, eu_feed_additive, hs, fao_cpc, asfis. */
export type TaxonomyCodes = Partial<Record<'eu_feed_catalogue' | 'eu_feed_additive' | 'hs' | 'fao_cpc' | 'asfis', string>>

type Taxonomy = ApiResponse<'/taxonomy'>
export type ApiCommodity = Taxonomy['commodities'][number]
export type ApiIngredient = Taxonomy['ingredients'][number]
export type ImportResult = ApiResponse<'/taxonomy/imports', 'post'>
export type TaxonomyChange = ApiResponse<'/taxonomy/changes'>['items'][number]
export type ParentCompany = ApiResponse<'/parent-companies'>['items'][number]
export type Reviewers = ApiResponse<'/suppliers/{id}/reviewers'>
export type Person = Reviewers['level1'][number] & { kind?: string; roles?: StaffRole[] }
export interface Supplier { id: string; name: string; country_code: string | null; parent_company_id?: string | null }

const keys = {
  taxonomy: ['buyer', 'taxonomy'] as const,
  changes: ['buyer', 'taxonomy-changes'] as const,
  parents: ['buyer', 'parent-companies'] as const,
  suppliers: ['buyer', 'suppliers'] as const,
  people: ['buyer', 'people'] as const,
  reviewers: (id: string) => ['buyer', 'reviewers', id] as const,
  help: ['buyer', 'help-requests'] as const,
}

export const useTaxonomy = () => useQuery({
  queryKey: keys.taxonomy,
  queryFn: () => get<Taxonomy>('/taxonomy?includeInactive=true'),
})

export const useTaxonomyChanges = () => useQuery({
  queryKey: keys.changes,
  queryFn: () => get<ApiResponse<'/taxonomy/changes'>>('/taxonomy/changes').then((r) => r.items),
})

function useWrite<A>(run: (a: A) => Promise<unknown>, ...invalidate: readonly (readonly string[])[]) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => Promise.all(invalidate.map((queryKey) => client.invalidateQueries({ queryKey }))),
  })
}

export const useAddCommodity = () => useWrite(
  (b: ApiBody<'/taxonomy/commodities'>) => post('/taxonomy/commodities', b), keys.taxonomy)
export const useEditCommodity = () => useWrite(
  ({ id, ...b }: { id: string } & ApiBody<'/taxonomy/commodities/{id}', 'patch'>) => patch(`/taxonomy/commodities/${id}`, b), keys.taxonomy)
export const useAddIngredient = () => useWrite(
  (b: ApiBody<'/taxonomy/ingredients'>) => post('/taxonomy/ingredients', b), keys.taxonomy)
export const useEditIngredient = () => useWrite(
  ({ id, ...b }: { id: string } & ApiBody<'/taxonomy/ingredients/{id}', 'patch'>) => patch(`/taxonomy/ingredients/${id}`, b), keys.taxonomy)

/** A spreadsheet checked (dryRun) or loaded. A file with problems answers 400 with them, which is a result here, not an error. */
export function useImportTaxonomy() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, dryRun }: { file: File; dryRun: boolean }): Promise<ImportResult> => {
      const form = new FormData()
      form.append('file', file)
      try {
        return await upload<ImportResult>(`/taxonomy/imports${dryRun ? '?dryRun=true' : ''}`, form)
      } catch (e) {
        const body = (e as { body?: ImportResult }).body
        if (body?.issues) return body
        throw e
      }
    },
    onSuccess: (r) => { if (r.applied) void client.invalidateQueries({ queryKey: keys.taxonomy }) },
  })
}

export const downloadTaxonomy = () => api<Blob | string>('/taxonomy/export.csv')
export const useDecideChange = () => useWrite(
  ({ id, ...b }: { id: string } & ApiBody<'/taxonomy/changes/{id}', 'patch'>) => patch(`/taxonomy/changes/${id}`, b),
  keys.changes, keys.taxonomy)

export const useParentCompanies = () => useQuery({
  queryKey: keys.parents,
  queryFn: () => get<ApiResponse<'/parent-companies'>>('/parent-companies').then((r) => r.items),
})
/** The polygon-checks supplier list (published, snake_case). */
export const useSuppliers = () => useQuery({
  queryKey: keys.suppliers,
  queryFn: () => get<{ suppliers?: Supplier[] } | Supplier[]>('/suppliers').then((r) => (Array.isArray(r) ? r : r.suppliers ?? [])),
})
/** The organisation's own people (the admin screens' route, snake_case), as reviewers. */
export const useStaff = () => useQuery({
  queryKey: keys.people,
  queryFn: () => get<{ users: { id: string; full_name: string | null; email: string | null; kind: string; roles?: StaffRole[] }[] }>('/org/users')
    .then((r) => r.users.filter((u) => u.kind === 'staff').map((u): Person => ({ id: u.id, fullName: u.full_name, email: u.email, kind: u.kind, roles: u.roles }))),
})
export const useReviewers = (supplierId: string | null) => useQuery({
  queryKey: keys.reviewers(supplierId ?? ''),
  queryFn: () => get<Reviewers>(`/suppliers/${supplierId}/reviewers`),
  enabled: Boolean(supplierId),
})

export const useAddParent = () => useWrite((b: ApiBody<'/parent-companies'>) => post('/parent-companies', b), keys.parents)
export const useRenameParent = () => useWrite(({ id, ...b }: { id: string; name: string }) => patch(`/parent-companies/${id}`, b), keys.parents)
/** Renames a parent company, moves it under another (or to the top), or changes its reference. */
export const useEditParent = () => useWrite(
  ({ id, ...b }: { id: string; name?: string; parentId?: string | null; externalRef?: string | null }) => patch(`/parent-companies/${id}`, b),
  keys.parents)
/** Renames a legal entity (the published supplier route). */
export const useRenameSupplier = () => useWrite(
  ({ id, name }: { id: string; name: string }) => patch(`/suppliers/${id}`, { name }), keys.suppliers, keys.parents)
export const useSetParent = () => useWrite(
  ({ supplierId, parentId }: { supplierId: string; parentId: string | null }) => put(`/suppliers/${supplierId}/parent-company`, { parentCompanyId: parentId }),
  keys.parents, keys.suppliers)
export function useSetReviewers(supplierId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (b: { level1: string[]; level2: string[] }) => put(`/suppliers/${supplierId}/reviewers`, b),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.reviewers(supplierId) }),
  })
}

// Campaigns (design brief screen 10).

export type CampaignRow = ApiResponse<'/campaigns'>['items'][number]
export type Assignment = ApiResponse<'/campaigns/{id}/assignments'>['items'][number]
export type AssignmentImport = ApiResponse<'/campaigns/{id}/assignment-imports', 'post'> & {
  suppliers: { added: number; existing: number }
  materials: { added: number; changed: number; unchanged: number }
  assignments: { added: number; existing: number }
  reviewers: { added: number }
  logins: { added: number }
  prefilled: number
}
export type Readiness = ApiResponse<'/campaigns/{id}/readiness'>
export type OpenBlocker = Readiness['blockers'][number]
export type Reminder = ApiResponse<'/campaigns/{id}/reminders'>['items'][number]

const campaignKeys = {
  list: ['buyer', 'campaigns'] as const,
  assignments: (id: string) => ['buyer', 'campaign-assignments', id] as const,
  readiness: (id: string) => ['buyer', 'campaign-readiness', id] as const,
  reminders: (id: string) => ['buyer', 'campaign-reminders', id] as const,
}

export const useCampaigns = () => useQuery({
  queryKey: campaignKeys.list,
  queryFn: () => get<ApiResponse<'/campaigns'>>('/campaigns').then((r) => r.items),
})

export const useAssignments = (id: string) => useQuery({
  queryKey: campaignKeys.assignments(id),
  queryFn: () => get<ApiResponse<'/campaigns/{id}/assignments'>>(`/campaigns/${id}/assignments`).then((r) => r.items),
})

/** What stops the campaign opening, before anyone tries; and what the schedule last found. */
export const useReadiness = (id: string) => useQuery({
  queryKey: campaignKeys.readiness(id),
  queryFn: () => get<Readiness>(`/campaigns/${id}/readiness`),
})

export const useReminders = (id: string) => useQuery({
  queryKey: campaignKeys.reminders(id),
  queryFn: () => get<ApiResponse<'/campaigns/{id}/reminders'>>(`/campaigns/${id}/reminders`).then((r) => r.items),
})

/** Reminds every supplier that has not submitted (or the ones named) now. */
export function useSendReminders(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (supplierIds?: string[]) =>
      post<ApiResponse<'/campaigns/{id}/reminders', 'post'>>(`/campaigns/${id}/reminders`, supplierIds?.length ? { supplierIds } : {}),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: campaignKeys.reminders(id) }),
      client.invalidateQueries({ queryKey: campaignKeys.assignments(id) }),
    ]),
  })
}

export const useCreateCampaign = () => useWrite(
  (b: ApiBody<'/campaigns'>) => post<{ id: string }>('/campaigns', b), campaignKeys.list)

/** Renames, moves dates, sets the schedule, opens or closes. Opening can be refused with the suppliers that block it. */
export function useUpdateCampaign(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (b: ApiBody<'/campaigns/{id}', 'patch'>) => {
      try {
        await patch(`/campaigns/${id}`, b)
        return { blockers: [] as OpenBlocker[] }
      } catch (e) {
        const body = (e as { body?: { blockers?: OpenBlocker[] } }).body
        if (body?.blockers) return { blockers: body.blockers }
        throw e
      }
    },
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: campaignKeys.list }),
      client.invalidateQueries({ queryKey: campaignKeys.assignments(id) }),
      client.invalidateQueries({ queryKey: campaignKeys.readiness(id) }),
    ]),
  })
}

export function useImportAssignments(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, dryRun }: { file: File; dryRun: boolean }): Promise<AssignmentImport> => {
      const form = new FormData()
      form.append('file', file)
      try {
        return await upload<AssignmentImport>(`/campaigns/${id}/assignment-imports${dryRun ? '?dryRun=true' : ''}`, form)
      } catch (e) {
        const body = (e as { body?: AssignmentImport }).body
        if (body?.issues) return body
        throw e
      }
    },
    onSuccess: (r) => {
      if (r.applied) {
        void client.invalidateQueries({ queryKey: campaignKeys.assignments(id) })
        void client.invalidateQueries({ queryKey: campaignKeys.list })
        void client.invalidateQueries({ queryKey: campaignKeys.readiness(id) })
        void client.invalidateQueries({ queryKey: ['buyer', 'campaign-demand', id] })
      }
    },
  })
}

export type AddToCampaign = ApiBody<'/campaigns/{id}/assignments', 'post'>
export type AddToCampaignResult = ApiResponse<'/campaigns/{id}/assignments', 'post'>
export type Material = ApiResponse<'/materials'>['items'][number]

/** A supplier's materials, assigned or not. */
export const useMaterials = (supplierId: string | null) => useQuery({
  queryKey: ['buyer', 'materials', supplierId ?? ''],
  queryFn: () => get<ApiResponse<'/materials'>>(`/materials?supplierId=${supplierId}`).then((r) => r.items),
  enabled: Boolean(supplierId),
})

/** The campaign's queries, and the lists a supplier added from the form appears in. */
function campaignWritten(client: ReturnType<typeof useQueryClient>, id: string) {
  return Promise.all([
    client.invalidateQueries({ queryKey: campaignKeys.assignments(id) }),
    client.invalidateQueries({ queryKey: campaignKeys.list }),
    client.invalidateQueries({ queryKey: campaignKeys.readiness(id) }),
    client.invalidateQueries({ queryKey: keys.suppliers }),
    client.invalidateQueries({ queryKey: keys.parents }),
    client.invalidateQueries({ queryKey: ['buyer', 'materials'] }),
    client.invalidateQueries({ queryKey: ['buyer', 'reviewers'] }),
    client.invalidateQueries({ queryKey: ['buyer', 'campaign-demand', id] }),
  ])
}

/**
 * One supplier added to the campaign from the form, with its materials,
 * reviewers and contacts: what one supplier's rows of the spreadsheet do.
 * Only adds. Refused with the issues in the error's message.
 */
export function useAddToCampaign(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (b: AddToCampaign) => post<AddToCampaignResult>(`/campaigns/${id}/assignments`, b),
    onSuccess: () => campaignWritten(client, id),
  })
}

/**
 * Sets the ingredient a fixed material is (null clears it). Open answers its
 * supplier has not given yet start from it.
 */
export function useSetMaterialIngredient(campaignId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ingredientId }: { id: string; ingredientId: string | null }) => patch(`/materials/${id}`, { ingredientId }),
    onSuccess: () => campaignWritten(client, campaignId),
  })
}

/** Refreshes what the campaign screen shows, after writes made on other routes (a supplier's reviewers). */
export function useCampaignRefresh(id: string) {
  const client = useQueryClient()
  return () => campaignWritten(client, id)
}

/** Replaces the materials one supplier answers for in the campaign; materials taken off lose their answers. */
export function useSetAssignment(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ supplierId, materialIds, volumes }: { supplierId: string; materialIds: string[]; volumes?: Record<string, number> }) =>
      put(`/campaigns/${id}/assignments`, { assignments: [{ supplierId, materialIds, ...(volumes && Object.keys(volumes).length ? { volumes } : {}) }] }),
    onSuccess: () => campaignWritten(client, id),
  })
}

export type DemandCoverage = ApiResponse<'/campaigns/{id}/demand-targets'>
export type DemandLine = DemandCoverage['items'][number]
export type DemandSubtotal = DemandCoverage['commodities'][number]
const demandKey = (id: string) => ['buyer', 'campaign-demand', id] as const

/** What the campaign needs of each ingredient, how much of it is assigned, traced, submitted and approved, and commodity subtotals. */
export const useDemand = (campaignId: string | undefined) => useQuery({
  queryKey: demandKey(campaignId ?? ''),
  queryFn: () => get<DemandCoverage>(`/campaigns/${campaignId}/demand-targets`),
  enabled: Boolean(campaignId),
})

export function useSetDemand(campaignId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ ingredientId, demandT }: { ingredientId: string; demandT: number }) =>
      put<DemandCoverage>(`/campaigns/${campaignId}/demand-targets/${ingredientId}`, { demandT }),
    onSuccess: (r) => client.setQueryData(demandKey(campaignId), r),
  })
}

export function useRemoveDemand(campaignId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (ingredientId: string) => del(`/campaigns/${campaignId}/demand-targets/${ingredientId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: demandKey(campaignId) }),
  })
}

export const downloadAssignmentTemplate = (id: string) => api<Blob | string>(`/campaigns/${id}/assignments/template.csv`)

/** One origin as the campaign's origins map shows it: where it is, its check, its farms, and every use of it. */
export interface CampaignOrigin {
  id: string
  name: string
  /** `farms`: made of the farms linked to it; its check is its plots'. */
  kind: 'point' | 'area' | 'farms'
  countryCode: string
  geometry: GeoJSON.Point | GeoJSON.Polygon
  check: { state: 'passed' } | { state: 'failed'; reason: string } | { state: 'pending' }
  parentCompany: { id: string; name: string } | null
  supply: import('@agribot/sourcing-model').SupplySummary | null
  uses: { supplierId: string; supplier: string; materialId: string; material: string; ingredientId: string; ingredient: string; commodity: string; volumeSharePct: number; tonnes: number; submissionId: string; submissionStatus: string }[]
}

export const useCampaignOrigins = (campaignId: string | undefined, filter: { commodityId?: string; parentCompanyId?: string; status?: string; check?: string }) => useQuery({
  queryKey: ['buyer', 'campaign-origins', campaignId, filter],
  enabled: Boolean(campaignId),
  queryFn: () => {
    const q = new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => Boolean(e[1])))
    return get<{ items: CampaignOrigin[] }>(`/campaigns/${campaignId}/origins${q.size ? `?${q}` : ''}`).then((r) => r.items)
  },
})

// Supplier help requests (the RFI's "supplier requests").

export type HelpStatusFilter = 'active' | 'open' | 'answered' | 'closed' | 'all'

export const useHelpRequests = (status: HelpStatusFilter) => useQuery({
  queryKey: [...keys.help, status],
  queryFn: () => get<{ items: HelpRequest[] }>(`/help-requests?status=${status}`).then((r) => r.items),
})

export function useAnswerHelpRequest() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, message }: { id: string; message: string }) => post<HelpRequest>(`/help-requests/${id}/messages`, { message }),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: keys.help }),
      client.invalidateQueries({ queryKey: ['sourcing', 'dashboard'] }),
    ]),
  })
}

export function useCloseHelpRequest() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => patch<HelpRequest>(`/help-requests/${id}`, { status: 'closed' }),
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: keys.help }),
      client.invalidateQueries({ queryKey: ['sourcing', 'dashboard'] }),
    ]),
  })
}

// Deleting a campaign, a supplier or a parent company, after a preview of what goes.

export type DeletionKind = 'campaign' | 'supplier' | 'parent' | 'assignment'
/** `assignment`: the supplier `id` taken out of campaign `campaignId`. */
export interface DeletionTarget { kind: DeletionKind; id: string; campaignId?: string }
/** GET …/deletion-preview: what goes, what stays or moves, and whether it is refused. */
export interface DeletionPreview {
  name: string
  removes: Record<string, number>
  keeps: Record<string, number>
  blocked: string | null
  confirmByName: boolean
}

/** Every query the workspace keeps (here, hooks.ts, api-store.ts) sits under one of these. */
const QUERY_ROOTS = ['buyer', 'sourcing']

const deletionPath = ({ kind, id, campaignId }: DeletionTarget) =>
  kind === 'campaign' ? `/campaigns/${id}`
    : kind === 'supplier' ? `/suppliers/${id}`
      : kind === 'assignment' ? `/campaigns/${campaignId}/assignments/${id}`
        : `/parent-companies/${id}`

export const useDeletionPreview = (target: DeletionTarget) => useQuery({
  queryKey: ['buyer', 'deletion-preview', target.kind, target.id, target.campaignId],
  queryFn: () => get<DeletionPreview>(`${deletionPath(target)}/deletion-preview`),
  staleTime: 0,
  gcTime: 0,
})

/** Deletes, then refreshes everything the buyer workspace has loaded: a deletion reaches most screens. */
export function useDelete() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (target: DeletionTarget) => del(deletionPath(target)),
    // Not awaited: the screen moves on straight away, and what it left refreshes behind it.
    onSuccess: () => { for (const root of QUERY_ROOTS) void client.invalidateQueries({ queryKey: [root] }) },
  })
}
